import { ArrowDownCircleIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType, setLoanData } from 'actions'
import cloneDeep from 'clone-deep'
import { IInputsTableWithNoApplyData, InputsTableWithNoApply } from 'components/InputsTable'
import {
  customOptionLabels,
  editableFieldOptions,
  FieldOrigin,
  ILoanOriginSection,
  InputType,
  isInputToTriggerBlur,
  IVisibleProp,
} from 'config'
import { useLockContext } from 'context/LockContext'
import { getDefaultLoanProcessData } from 'pages/LoanStructure/constant'
import { ILoan, ILoanProcess } from 'pages/LoanStructure/interfaces'
import { convertNullValue } from 'pages/Resources/GuidelineOverlay/logic'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getGeoNameList, getLoanProcessRate, updateLoanFields } from 'services'
import { combinedMonthlyExpense, combinedPropertyMonthlyExpense } from 'services/pdfs/utils'
import { canEditLoanApplication, getItemsFromFullAddress, InputConvert, InputValidate, openAuditLog } from 'utils'
import { isReadOnlyByEditPermission, isViewOnlyByLogic } from 'utils/loan'
import { resolveLinkedFieldValue } from 'utils/loanLinkedField'
import { RenderInput } from 'utils/RenderInput'
import { setBorrowerSeparator } from 'utils/setLoanNumber'

import { AdditionalPropertyInformation } from '../AdditionalPropertyInformation'
import { getFailedAdditionalPropertyInfo } from '../AdditionalPropertyInformation/logics'
import { BorrowerSeperator } from '../config'
import {
  defaultInputs,
  ignoredFields,
  propertyExtraSectionAdminTitles,
  propertyExtraSectionKeys,
  propertyExtraSectionTitles,
} from './constants'
import { giftsOrGrantsColumnHeaders, giftsOrGrantsColumnKeys, giftsOrGrantsInputs } from './giftsOrGrants.constants'
import { initFields, propertyInfoValidate, visiblePropertyFieldsLogic } from './logic'
import {
  otherMortgageLoansColumnHeaders,
  otherMortgageLoansColumnKeys,
  otherMortgageLoansInputs,
} from './otherMortgageLoans.constants'

interface IProps {
  setLoading: (value: boolean) => void
  /**
   * Rendered between the property fields and the Additional Property Addresses table, with this
   * section's own edit permission. Servicing hangs the additional-properties editor here; in
   * origination the list belongs to Loan Structure, which prices against it, so nothing is passed.
   */
  beforeAdditionalProperties?: (canEdit: boolean) => ReactNode
}

export function PropertyInformation(props: IProps) {
  const [inputs, setInputs] = useState<Record<string, InputType>>(initFields(defaultInputs()))
  const [visibelFields, setVisibleFields] = useState(visiblePropertyFieldsLogic())
  const [fieldValueChanged, setFieldValueChanged] = useState(false)
  const [loanProcess, setLoanProcess] = useState<ILoanProcess>(getDefaultLoanProcessData())
  const [propertyCountyOptions, setPropertyCountyOptions] = useState([])

  const dispatch = useDispatch()
  const { loan, loanDetail, fieldProperties, masterOptionProps, profile, borrowers } = useSelector((state: any) => {
    return {
      loan: state.loan as ILoan,
      loanDetail: state.loanDetail,
      fieldProperties: ((state.globalConfigReducer[GlobalConfigType.LoanFields] || {})[
        FieldOrigin.PropertyInformation
      ] || {}) as Record<string, IVisibleProp>,
      // Loan Structure owns the option lists a tenant renames; every section reads them from there.
      masterOptionProps: ((state.globalConfigReducer[GlobalConfigType.LoanFields] || {})[FieldOrigin.LoanStructure] ||
        {}) as Record<string, IVisibleProp>,
      profile: state.auth.profile,
      borrowers: state.borrower,
    }
  })
  const templateNumber = loanDetail.templateNumber

  setBorrowerSeparator(BorrowerSeperator.BORROWER)

  const { loanFieldSections } = useSelector((state: any) => ({
    loanFieldSections: (((state.globalConfigReducer[GlobalConfigType.LoanFields] || {})['sections'] || {})[
      FieldOrigin.PropertyInformation
    ] || []) as ILoanOriginSection[],
  }))

  const freeSectionFields = useMemo(() => {
    return Object.keys(inputs).filter((key) => {
      return !loanFieldSections.find((section) => section.fields.includes(key))
    })
  }, [loanFieldSections, inputs])

  // Extra sections (other mortgages / rental / gifts) render as dedicated blocks below —
  // exclude them from the configurable section grid so hidden ones don't leave empty titles.
  const gridLoanFieldSections = useMemo(() => {
    return loanFieldSections.filter((section) => {
      if (!section.fields.length) return false
      const onlyExtra = section.fields.every((key) => (propertyExtraSectionKeys as readonly string[]).includes(key))
      return !onlyExtra
    })
  }, [loanFieldSections])

  useEffect(() => {
    updatePropertyCountyOptions()
  }, [loan.subjectPropertyAddress])

  const updatePropertyCountyOptions = async () => {
    const propertyAddressObj: any = await getItemsFromFullAddress(loan.subjectPropertyAddress)
    const list = propertyAddressObj.postal_code ? await getGeoNameList(propertyAddressObj.postal_code) : []
    if (!list) return
    const options = list
      .map((v: any) => v.adminName2)
      .filter((v: string) => !!v)
      .map((v: string) => (v.endsWith('County') ? v : `${v} County`))
    setPropertyCountyOptions(options)
  }

  const init = async () => {
    props.setLoading(true)
    const nInputs = propertyInfoValidate()
    const propertyAddressObj: any = await getItemsFromFullAddress(loan.subjectPropertyAddress)
    if (propertyAddressObj.state === 'NY') {
      nInputs['nyBlock'].visible = true
      nInputs['nyLot'].visible = true
    }
    setInputs(nInputs)
    props.setLoading(false)
  }

  useEffect(() => {
    init()

    getLoanProcessRate().then((res) => {
      if (res.success) setLoanProcess(res.data)
    })
  }, [])

  useEffect(() => {
    setVisibleFields(visiblePropertyFieldsLogic())
  }, [inputs, loan.proposedOccupancy])

  const failedAdditionalPropertyInfo = getFailedAdditionalPropertyInfo()

  const onChange = async (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    setInputs(newInputs)
    setFieldValueChanged(true)

    dispatch(setLoanData({ key: key, data: value }))

    if (isInputToTriggerBlur(newInputs[key])) {
      await updateLoanFields({ [key]: value }, !!templateNumber)
    }
  }

  const onBlur = async (key: string) => {
    if (!fieldValueChanged) return
    setFieldValueChanged(false)
    if (inputs[key].error) return
    props.setLoading(true)
    const reqBody = {
      [key]: (loan as any)[key],
    }
    if (key == 'subjectPropertyAddress') reqBody['propertyCounty'] = loan['propertyCounty']

    await updateLoanFields(reqBody, !!templateNumber)
    props.setLoading(false)
    propertyInfoValidate(true, true)
  }

  const showHistory = (key: string) => {
    const input = inputs[key]
    const options = {
      table: 'Loan',
      field: key,
      keys: {
        field: key,
      },
      renderValue: input.options,
    }
    openAuditLog(options)
  }

  const { resolveCanEdit } = useLockContext()
  const propertySectionScope = {
    type: 'section' as const,
    borrowerSeperator: 'borrower',
    fieldOrigin: FieldOrigin.PropertyInformation,
  }
  const canEdit = resolveCanEdit(
    propertySectionScope,
    canEditLoanApplication() && (!profile.isBorrower || borrowers.borrower.email === profile.email),
  )

  const otherMortgageLoansData: IInputsTableWithNoApplyData<Record<string, any>> = useMemo(
    () => ({
      rows: Array.isArray((loan as any)?.otherMortgageLoans?.rows) ? (loan as any)?.otherMortgageLoans?.rows : [],
      doNotApply: Boolean((loan as any)?.otherMortgageLoans?.doNotApply),
    }),
    [(loan as any)?.otherMortgageLoans],
  )

  const onUpdateOtherMortgageLoans = async (data: IInputsTableWithNoApplyData<Record<string, any>>) => {
    if (!canEdit) return
    props.setLoading(true)
    dispatch(setLoanData({ key: 'otherMortgageLoans', data }))
    await updateLoanFields({ otherMortgageLoans: data }, !!templateNumber)
    props.setLoading(false)
  }

  const giftsOrGrantsData: IInputsTableWithNoApplyData<Record<string, any>> = useMemo(
    () => ({
      rows: Array.isArray((loan as any)?.giftsOrGrants?.rows) ? (loan as any).giftsOrGrants.rows : [],
      doNotApply: Boolean((loan as any)?.giftsOrGrants?.doNotApply),
    }),
    [(loan as any)?.giftsOrGrants],
  )

  const onUpdateGiftsOrGrants = async (data: IInputsTableWithNoApplyData<Record<string, any>>) => {
    if (!canEdit) return
    props.setLoading(true)
    dispatch(setLoanData({ key: 'giftsOrGrants', data }))
    await updateLoanFields({ giftsOrGrants: data }, !!templateNumber)
    props.setLoading(false)
  }

  const getOtherMortgageLoansCellValue = (columnKey: string, row: Record<string, any>) => {
    if (columnKey === 'concurrentClosing') {
      if (row.concurrentClosing && row.heloc) return 'Concurrent / HELOC'
      if (row.concurrentClosing) return 'Concurrent'
      if (row.heloc) return 'HELOC'
      return ''
    }
    return undefined
  }

  const isExtraSectionVisible = (key: string) => {
    const prop = fieldProperties[key]
    if (!prop || prop.visible === false) return false
    return visibelFields.includes(key)
  }

  const resolveExtraSectionTitle = (key: typeof propertyExtraSectionKeys[number]) => {
    const configured = fieldProperties[key]?.title
    if (configured && configured !== propertyExtraSectionAdminTitles[key]) return configured
    return propertyExtraSectionTitles[key]
  }

  const renderValidateMessage = () => {
    if (!failedAdditionalPropertyInfo.length) return null

    return (
      <div className="text-left bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 text-[15px]">
        <div className="mb-2">
          This loan has Additional Subject Property Addresses and below addresses are required to validate detailed
          information.
          <br />
          <p className="flex gap-1">
            Please{' '}
            <span
              className="flex gap-1 font-bold cursor-pointer"
              onClick={() => window.scrollTo({ top: window.innerHeight })}
            >
              Scroll down <ArrowDownCircleIcon className="w-4 h-4" />
            </span>{' '}
            and complete the information.
          </p>
        </div>
        {failedAdditionalPropertyInfo.map((item, index) => (
          <p key={index} className="font-semibold">
            - {item.propertyAddress}
          </p>
        ))}
      </div>
    )
  }

  const renderInput = (key: string, index: number) => {
    if (ignoredFields.includes(key)) return null

    let input = inputs[key]
    if (!input || input.visible === false) return null
    if (!input.readOnly && visibelFields.indexOf(key) === -1) return null
    input.history = true
    input.value = (loan as any)[key]

    const linked = resolveLinkedFieldValue(fieldProperties[key], loan)
    if (linked.resolved) {
      const sourceKey = fieldProperties[key]?.linkedField?.fieldKey
      const sourceValue = sourceKey ? (loan as any)[sourceKey] : undefined
      input.value =
        input.inputType === 'select' && sourceValue !== undefined && sourceValue !== null && sourceValue !== ''
          ? sourceValue
          : linked.value
      input.readOnly = true
    }
    if (isViewOnlyByLogic(fieldProperties[key]?.viewOnlyLogic)) input.readOnly = true
    if (!input.readOnly && isReadOnlyByEditPermission(fieldProperties[key]?.editPermissionAccountTypes))
      input.readOnly = true

    if (input.disabled) {
    } else {
      input.disabled = !canEdit
    }

    if (fieldProperties[key] && fieldProperties[key].title) input.title = fieldProperties[key].title || input.title
    if (fieldProperties[key] && fieldProperties[key].type) input.type = (fieldProperties[key].type || input.type) as any
    if (fieldProperties[key]?.required !== undefined) input.required = fieldProperties[key].required

    if (key == 'propertyCounty') (input as any).options = propertyCountyOptions

    const renamed = customOptionLabels(editableFieldOptions(key, fieldProperties, masterOptionProps))
    if (renamed) (input as any).options = renamed

    if (input.inputType == 'FileTable') input.span = 2

    let rlt: any = [
      <div className={`input md:col-span-${input.span || 1}`} key={index}>
        <RenderInput
          input={input}
          Key={key}
          onChange={onChange}
          showHistory={showHistory}
          onBlur={onBlur}
          lockScope={{
            type: 'field',
            borrowerSeperator: 'borrower',
            fieldOrigin: FieldOrigin.PropertyInformation,
            fieldKey: key,
          }}
        />
      </div>,
    ]
    if (key === 'otherTax') {
      const pitis = !loan.isMultipleProperties
        ? combinedMonthlyExpense(loan, loanProcess)
        : combinedPropertyMonthlyExpense(loan, loan, loanProcess)
      rlt.push(
        <div className={`input md:col-span-${input.span || 1}`} key={`monthlyPayment-${index}`}>
          <RenderInput
            input={{
              title: 'Proposed Monthly Property Payment',
              inputType: 'text',
              type: 'thousandSep',
              prefix: '$',
              value: pitis.sum,
              readOnly: true,
            }}
            Key={key}
            onChange={() => {}}
          />
        </div>,
      )

      const isVisibleCalculatedDscr = visibelFields.includes('calculatedDscr')
      if (isVisibleCalculatedDscr)
        rlt.push(
          <div className={`input md:col-span-${input.span || 1}`} key={`dscr-${index}`}>
            <RenderInput
              input={{
                title: 'Calculated DSCR',
                inputType: 'text',
                type: 'text',
                prefix: '%',
                ...((fieldProperties['calculatedDscr'] || {}) as any),
                value: convertNullValue(pitis['dscr']),
                readOnly: true,
              }}
              Key={key}
              onChange={() => {}}
            />
          </div>,
        )
    }
    return rlt
  }

  const sectionCount = gridLoanFieldSections.length + (freeSectionFields.length ? 1 : 0)

  return (
    <div>
      {renderValidateMessage()}
      <div className="grid gap-4 md:grid-cols-2 grid-cols-1 mb-5">
        {gridLoanFieldSections.map(
          (section) =>
            section.fields.length != 0 && (
              <div className={`col-span-full grid gap-4 md:grid-cols-2 grid-cols-1 mb-3`}>
                {sectionCount > 1 && (
                  <div className="col-span-full">
                    <RenderInput
                      input={{ inputType: 'section', title: section.title || '' }}
                      Key={`${section.id}`}
                      onChange={() => {}}
                    />
                  </div>
                )}
                {section.fields.map((key, index) => renderInput(key, index))}
              </div>
            ),
        )}
        {freeSectionFields.map((key, index) => renderInput(key, index))}
      </div>
      {isExtraSectionVisible('otherMortgageLoans') && (
        <InputsTableWithNoApply
          title={resolveExtraSectionTitle('otherMortgageLoans')}
          inputs={otherMortgageLoansInputs}
          data={otherMortgageLoansData}
          onUpdate={onUpdateOtherMortgageLoans as any}
          columnKeys={[...otherMortgageLoansColumnKeys]}
          columnHeaders={otherMortgageLoansColumnHeaders}
          addRowTitle="Add Mortgage Loan"
          editRowTitle="Edit Mortgage Loan"
          getCellValue={getOtherMortgageLoansCellValue}
          readOnly={!canEdit}
        />
      )}

      {isExtraSectionVisible('rentalIncome') && (
        <div className="mb-5">
          <div className="mb-3">
            <RenderInput
              input={{
                inputType: 'section',
                title: resolveExtraSectionTitle('rentalIncome'),
                additionalElements: (
                  <div className="flex justify-end">
                    <RenderInput
                      input={{
                        inputType: 'checkbox',
                        title: fieldProperties.rentalIncomeDoesNotApply?.title || 'Do Not Apply',
                        value: Boolean((loan as any)?.rentalIncomeDoesNotApply),
                        disabled: !canEdit,
                      }}
                      Key="rentalIncomeDoesNotApply"
                      onChange={async (_key: string, value: boolean) => {
                        dispatch(setLoanData({ key: 'rentalIncomeDoesNotApply', data: value }))
                        await updateLoanFields({ rentalIncomeDoesNotApply: value }, !!templateNumber)
                      }}
                    />
                  </div>
                ),
              }}
              Key="rentalIncome-section"
              onChange={() => {}}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2 grid-cols-1">
            {(['rentalGrossIncome', 'rentalVacancyFactor', 'rentalPiti', 'rentalCashFlow'] as const).map((key) => {
              const input = inputs[key]
              if (!input) return null
              let fieldInput = {
                ...input,
                value: (loan as any)[key],
                disabled: !canEdit || Boolean((loan as any)?.rentalIncomeDoesNotApply),
              }
              if (fieldProperties[key]?.title) fieldInput.title = fieldProperties[key].title || fieldInput.title
              if (fieldProperties[key]?.required !== undefined) fieldInput.required = fieldProperties[key].required
              if (isViewOnlyByLogic(fieldProperties[key]?.viewOnlyLogic)) fieldInput.readOnly = true
              if (!fieldInput.readOnly && isReadOnlyByEditPermission(fieldProperties[key]?.editPermissionAccountTypes))
                fieldInput.readOnly = true
              return (
                <div className="input" key={key}>
                  <RenderInput input={fieldInput} Key={key} onChange={onChange} onBlur={onBlur} />
                </div>
              )
            })}
          </div>
        </div>
      )}

      {isExtraSectionVisible('giftsOrGrants') && (
        <InputsTableWithNoApply
          title={resolveExtraSectionTitle('giftsOrGrants')}
          inputs={giftsOrGrantsInputs}
          data={giftsOrGrantsData}
          onUpdate={onUpdateGiftsOrGrants as any}
          columnKeys={[...giftsOrGrantsColumnKeys]}
          columnHeaders={giftsOrGrantsColumnHeaders}
          addRowTitle="Add Gift or Grant"
          editRowTitle="Edit Gift or Grant"
          readOnly={!canEdit}
        />
      )}

      {props.beforeAdditionalProperties?.(canEdit)}

      <AdditionalPropertyInformation setLoading={props.setLoading} />
    </div>
  )
}
